import formatDateWithTimezone from "@insite/client-framework/Common/Utilities/formatDateWithTimezone";
import { trackCompletedOrder } from "@insite/client-framework/Common/Utilities/tracking";
import {
    addToChainAfter,
    ApiHandlerDiscreteParameter,
    HasOnError,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    chain,
    PopulateApiParameter,
    SendTracking,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";

const convertDateToApiFormat = (date: Date | null) => (date ? formatDateWithTimezone(date) : "");

interface PlaceOrderParameter {
    paymentMethod: string;
    poNumber: string;
    vatNumber?: string;
    saveCard: boolean;
    cardHolderName: string;
    cardNumber: string;
    cardType: string;
    expirationMonth: number;
    expirationYear: number;
    securityCode: string;
    useBillingAddress: boolean;
    address1: string;
    countryId: string;
    stateId: string;
    city: string;
    postalCode: string;
    onSuccess?: (cartId: string) => void;
    payPalToken?: string;
    payPalPayerId?: string;
    isPending?: boolean;
    isAdyenDropIn?: boolean;
    adyenPspReference?: string;
    accountHolderName?: string;
    accountNumber?: string;
    routingNumber?: string;
    payerId: string;
    maskedCardNumber: string;
}

type HandlerType = ApiHandlerDiscreteParameter<
    PlaceOrderParameter & HasOnError,
    UpdateCartApiParameter,
    CartResult,
    {
        cartToUpdate: Cart;
    }
>;

export const AddDataLayerPaymentMethod: HandlerType = props => {
    const state = props.getState();
    const affiliation = getBillToState(state, props.cartToUpdate?.billToId)?.value?.companyName;

    const dataLayer = (window as any).dataLayer || [];
    dataLayer.push({ ecommerce: null });
    const paymentdata = {
        event: "add_payment_info",
        ecommerce: {
            value: props.cartToUpdate?.orderGrandTotal.toFixed(2),
            payment_type: props.cartToUpdate.paymentMethod?.description,
            currency: "USD",
            affiliation: affiliation || "",
            items: props.cartToUpdate.cartLines?.map(cartLine => {
                return {
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    item_brand: cartLine.brand?.name,
                    price: cartLine.pricing?.actualPrice,
                    quantity: cartLine.qtyOrdered,
                };
            }),
        },
    };
    dataLayer.push(paymentdata);
};

export const SendTrackingCustom: HandlerType = props => {
    const updatedCart = { ...props.cartToUpdate, orderNumber: props.apiResult.cart.orderNumber };
    const session = getSession(props.getState());
    trackCompletedOrder(updatedCart, props.apiResult.billTo, session);
    const dataLayer = (window as any).dataLayer || [];
    dataLayer.push({ ecommerce: null });
    const paymentdata = {
        event: "purchase",
        ecommerce: {
            transaction_id: props.apiResult.cart.orderNumber,
            value: props.apiResult.cart.orderGrandTotal,
            tax: props.apiResult.cart.totalTax,
            shipping: props.apiResult.cart.shippingAndHandling,
            currency: "USD",
            items: props.cartToUpdate.cartLines?.map(cartLine => {
                return {
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    item_brand: cartLine.brand?.name,
                    price: cartLine.pricing?.actualPrice,
                    quantity: cartLine.qtyOrdered,
                };
            }),
        },
    };
    dataLayer.push(paymentdata);
};

addToChainAfter(chain, PopulateApiParameter, AddDataLayerPaymentMethod);
replaceInChain(chain, SendTracking, SendTrackingCustom);
